import api from "../../../../api";
import { AdLibCampaignWithReports } from "../../../../pages/AdvertiserDetailPage/CampaignsDashboard/components/LineItemsReportSectionComponents/CampaignLineItemsReportDashboard";
import { BrandgenCampaignPool, ReportTimeFrames } from "../../../../types/campaignTypes";

/**
 * CSV export of campaigns + their line items, ported from the legacy
 * CampaignsDialog so the redesigned dashboard produces the same file.
 */

export const EXPORT_TIME_FRAME_OPTIONS = [
  { label: "7 Days", value: ReportTimeFrames.DAYS_7 },
  { label: "30 Days", value: ReportTimeFrames.DAYS_30 },
  { label: "90 Days", value: ReportTimeFrames.DAYS_90 },
  { label: "All time", value: ReportTimeFrames.ALL_TIME },
];

export const DEFAULT_EXPORT_TIME_FRAME = ReportTimeFrames.ALL_TIME;

export const EXPORT_FILENAME = "BrandGen.io-Campaigns.csv";

const CAMPAIGN_HEADERS = [
  "Campaign Name",
  "Campaign Active",
  "Campaign Description",
  "Start Date",
  "End Date",
  "Campaign Daily Budget",
  "Campaign Budget",
  "Campaign Accounts",
  "Campaign Archived",
  "Time Frame",
];

const LINE_ITEM_HEADERS = ["Line Item Budget", "Line Item Active"];

const EXCLUDED_REPORT_KEYS = new Set([
  "beeswaxAdvertiserId",
  "beeswaxCampaignId",
  "beeswaxLineItemId",
  "brandgenLineItemReportId",
  "createdAt",
  "updatedAt",
  "nonBilledDataCost",
  "id",
  "adLibCampaignId",
  "brandgenCampaignId",
  "adLibAdvertiserId",
  "campaignName",
  "currencyCode",
  "date",
  "billedDataCost",
  "media_spend",
  "net_margin",
  "net_margin_percent",
  "revenue",
  "gross_margin",
  "gross_margin_percent",
  "active",
  "video_q1",
  "video_q3",
  "video_skips",
  "average_viewable_seconds",
  "invalid_impression_percent",
  "invalid_impressions",
  "measurable_impression_percent",
  "measurable_impressions",
  "measurable_invalid_impressions",
  "vcpm",
  "viewability_percent",
  "viewability_measurability_percent",
  "viewable_impressions",
  "viewable_seconds",
  "conversion_orders",
  "conversions_value",
  "postback_conversions",
  "reach",
  "video_midpoints",
  "click_through_conversions",
  "spend_per_conversion",
  "viewthrough_conversions",
  "inventory_cost",
  "video_companion_clicks",
  "video_companion_ctr",
  "video_companion_views",
]);

const convertToReadable = (str: string): string =>
  str
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const transformReportObject = (report: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(report)) {
    if (!EXCLUDED_REPORT_KEYS.has(key)) {
      result[convertToReadable(key)] = value;
    }
  }
  return result;
};

const fetchLineItemsWithReport = async (brandgenCampaignPoolId: string) => {
  const { payload } = await api.getLineItemReports(brandgenCampaignPoolId);
  return payload;
};

/**
 * Builds the row array handed to `csvDownload`. Each campaign contributes a
 * header row, one row per line item, and two blank spacer rows — matching the
 * legacy file layout exactly.
 */
export const buildCampaignExportRows = async (
  campaigns: BrandgenCampaignPool[],
  timeFrame: ReportTimeFrames,
): Promise<any[]> => {
  const exportData: any[] = [];

  /* eslint-disable no-await-in-loop */
  for (const campaignPool of campaigns) {
    const campaignData = {
      CampaignName: campaignPool.name,
      Active: campaignPool.active,
      Description: campaignPool.description,
      StartDate: campaignPool.start_date,
      EndDate: campaignPool.end_date,
      DailyBudget: `$${campaignPool.daily_budget}`,
      CampaignBudget: `$${campaignPool.campaign_budget}`,
      Accounts: campaignPool.accounts ? campaignPool.accounts : "0",
      Archived: campaignPool.archived,
    };

    const allLineItems = (await fetchLineItemsWithReport(campaignPool.brandgenCampaignPoolId)) ?? [];

    const lineItemsWithReports: AdLibCampaignWithReports[] = (Array.isArray(allLineItems) ? allLineItems : []).map(
      (item: any) => ({
        ...item,
        adLibCampaignReports: ((item.adLibCampaignReports ?? []) as Array<{ timeFrame: string }>).filter(
          (report) => report.timeFrame === timeFrame,
        ),
      }),
    );

    if (lineItemsWithReports.length > 0) {
      const reportsHeaders = Object.keys(
        transformReportObject(lineItemsWithReports[0]?.adLibCampaignReports?.[0] ?? {}),
      );

      exportData.push(["Line Item Name", ...CAMPAIGN_HEADERS, ...LINE_ITEM_HEADERS, ...reportsHeaders]);

      lineItemsWithReports.forEach((lineItem) => {
        const lineItemReport = lineItem.adLibCampaignReports?.[0]
          ? transformReportObject(lineItem.adLibCampaignReports[0])
          : {};

        exportData.push({
          LineItemName: lineItem.campaignName ?? "N/A",
          ...campaignData,
          "Time Frame": timeFrame,
          LineItemBudget: `$${lineItem.campaignBudget}`,
          LineItemActive: lineItem.active,
          ...lineItemReport,
        });
      });
    } else {
      exportData.push({ data: `No line items in ${campaignPool.name}` });
    }

    exportData.push({});
    exportData.push({});
  }
  /* eslint-enable no-await-in-loop */

  return exportData;
};
